import React, { useEffect, useState } from 'react';
import {
  Typography,
  Button,
  Box,
  Avatar,
  TextField,
  InputAdornment,
  Paper,
  MenuItem,
  InputLabel,
  Select,
  FormControl,
  DialogActions,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { useLazyGetCompanyEmployeesQuery } from '@diditusa/shared/api/companyApi';
import { useAuth } from '../../context/AuthContext';
import LoadingIndicator from '@diditusa/shared/web_components/LoadingIndicator/loadingIndicator';
import BasicTable from '@diditusa/shared/web_components/BasicTable/BasicTable';
import { Employee, CompanyRole, BasicTableHeader } from '@diditusa/shared/types/commonTypes';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import { useAddCompanyEmployeeMutation } from '@diditusa/shared/api/companyApi';
import { useNavigate } from 'react-router-dom';

const CompanyAgents: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const companyId = user?.company?.id;
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [inviteFormData, setInviteFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    companyRole: CompanyRole.EMPLOYEE,
  });

  const [getCompanyEmployees, { data: agents, ...getAgentsState }] =
    useLazyGetCompanyEmployeesQuery();

  const [addCompanyEmployee, addCompanyEmployeeState] = useAddCompanyEmployeeMutation();

  useEffect(() => {
    if (companyId) {
      getCompanyEmployees(companyId);
    }
  }, [companyId]);

  const handleOpenInviteModal = () => {
    setIsInviteModalOpen(true);
  };

  const handleCloseInviteModal = () => {
    setIsInviteModalOpen(false);
    setInviteFormData({
      email: '',
      firstName: '',
      lastName: '',
      companyRole: CompanyRole.EMPLOYEE,
    });
  };

  const handleInviteFormChange = (event: any) => {
    setInviteFormData({
      ...inviteFormData,
      [event.target.name]: event.target.value,
    });
  };

  const handleInvite = async () => {
    if (!companyId) {
      console.error('Company ID is missing.');
      return;
    }

    try {
      await addCompanyEmployee({
        companyId: companyId,
        req: {
          email: inviteFormData.email,
          firstName: inviteFormData.firstName,
          lastName: inviteFormData.lastName,
          companyRole: inviteFormData.companyRole,
        },
      }).unwrap();

      handleCloseInviteModal();
    } catch (error) {
      console.error('Error inviting employee:', error);
      // Handle error (e.g., display an error message)
    }
  };

  if (!user || !user.company) {
    return <h6>Error no company found</h6>;
  }

  if (getAgentsState.isLoading) {
    return <LoadingIndicator isVisible={!getAgentsState.isLoading} />;
  }

  const renderAgents = () => {
    if (getAgentsState.isError) {
      return (
        <Typography>{`Error loading Agents: ${(getAgentsState as any).data.message}`}</Typography>
      );
    }
    type EmployeeKey = keyof Employee;

    const headers: BasicTableHeader<EmployeeKey>[] = [
      { key: 'imageUrl', value: '' },
      { key: 'firstName', value: 'First Name' },
      { key: 'lastName', value: 'Last Name' },
      { key: 'email', value: 'Email' },
      { key: 'phoneNumber', value: 'Phone Number' },
      { key: 'companyRole', value: 'Role' },
    ];
    const getCustomComponentForCell = (field: EmployeeKey, idx: number) => {
      if (!agents) {
        return;
      }
      const c = agents[idx];
      if (field === 'imageUrl') {
        return (
          <Box
            sx={{ ':hover': { cursor: 'pointer' } }}
            onClick={() => navigate(`/company/agents/${c.id}`)}
          >
            {c.imageUrl ? (
              <Avatar src={c.imageUrl} alt={c.id} sx={{ mr: 2 }} />
            ) : (
              <CorporateFareIcon />
            )}
          </Box>
        );
      }

      if (field === 'phoneNumber') {
        return (
          <Button variant='outlined' sx={{ borderRadius: 5 }}>
            {c.phoneNumber}
          </Button>
        );
      }
    };
    return (
      <>
        {/* todo: implement search and bring this back */}
        {/* <Box p={2}>
          <TextField
            fullWidth
            variant='outlined'
            placeholder='Search Agents'
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon sx={{ color: '#637588' }} />
                </InputAdornment>
              ),
              sx: { bgcolor: '#f0f2f4', borderRadius: 2 },
            }}
          />
        </Box> */}

        <Paper sx={{ p: 2, mt: 2, height: '100%' }}>
          <BasicTable
            headers={headers}
            data={agents || []}
            getCustomComponentForField={getCustomComponentForCell}
          />
        </Paper>
      </>
    );
  };

  return (
    <Box minHeight='100vh' display='flex' flexDirection='row' bgcolor='white'>
      <Box flex={1} maxWidth={'90%'} mx='auto'>
        <Box
          display='flex'
          flexDirection={'row'}
          justifyContent='space-between'
          alignItems='center'
          p={2}
        >
          <Typography variant='h4' fontWeight={700} color='#111418'>
            Agents
          </Typography>
          <Button
            onClick={handleOpenInviteModal}
            variant='outlined'
            sx={{ bgcolor: '#f0f2f4', borderRadius: 2 }}
          >
            Add Agent
          </Button>
        </Box>
        {renderAgents()}
      </Box>

      <Dialog open={isInviteModalOpen} onClose={handleCloseInviteModal}>
        <DialogTitle>Add New Agent</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin='dense'
            name='firstName'
            label='First Name'
            type='text'
            fullWidth
            variant='standard'
            value={inviteFormData.firstName}
            onChange={handleInviteFormChange}
          />
          <TextField
            margin='dense'
            name='lastName'
            label='Last Name'
            type='text'
            fullWidth
            variant='standard'
            value={inviteFormData.lastName}
            onChange={handleInviteFormChange}
          />
          <TextField
            margin='dense'
            name='email'
            label='Email Address'
            type='email'
            fullWidth
            variant='standard'
            value={inviteFormData.email}
            onChange={handleInviteFormChange}
          />
          {/* <FormControl variant='standard' fullWidth>
            <InputLabel id='company-role-label'>Role</InputLabel>
            <Select
              labelId='company-role-label'
              id='companyRole'
              name='companyRole'
              value={inviteFormData.companyRole}
              onChange={handleInviteFormChange}
              label='Role'
            >
              <MenuItem value={CompanyRole.EMPLOYEE}>Employee</MenuItem>
              <MenuItem value={CompanyRole.MANAGER}>Manager</MenuItem>
            </Select>
          </FormControl> */}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseInviteModal}>Cancel</Button>
          <Button onClick={handleInvite}>Add</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompanyAgents;
